// ---------- low-level helpers ----------
const _basename = (p) => (p ? String(p).split(/[\\/]/).pop() : "");
const _first = (re, s) => {
  const m = s.match(re);
  return m ? m[1] : null;
};

// Find a block by name: supports `name {` or `name: {`
function _extractBlock(src, block) {
  const re = new RegExp(String.raw`(^|\n)\s*${block}\s*:?\s*\{`, "m");
  const m = src.match(re);
  if (!m) return null;
  let i = src.indexOf("{", m.index);
  if (i < 0) return null;
  let depth = 0;
  for (let j = i; j < src.length; j++) {
    const ch = src[j];
    if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) return src.slice(i + 1, j); // inside {...}
    }
  }
  return null;
}
function _extractNested(src, names) {
  let cur = src;
  for (const n of names) {
    cur = _extractBlock(cur, n);
    if (!cur) return null;
  }
  return cur;
}
const _getStr = (blk, key) =>
  _first(new RegExp(String.raw`^\s*${key}\s*:\s*"(.*?)"`, "m"), blk) ??
  _first(new RegExp(String.raw`^\s*${key}\s*:\s*'(.*?)'`, "m"), blk) ??
  _first(new RegExp(String.raw`^\s*${key}\s*:\s*([^\s#;]+)`, "m"), blk);
const _getNum  = (blk, key) =>
  _first(new RegExp(String.raw`^\s*${key}\s*:\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))`, "m"), blk);
const _getBool = (blk, key) => _first(new RegExp(String.raw`^\s*${key}\s*:\s*(true|false)`, "m"), blk);
const _getArr  = (blk, key) => {
  const raw = _first(new RegExp(String.raw`^\s*${key}\s*:\s*\[(.*?)\]`, "m"), blk);
  if (!raw) return null;
  return raw.split(",").map(s => s.trim()).filter(Boolean).join(", ");
};

// ---------- learning-rate helper ----------
function _parseLearningRate(optBlock) {
  const lrBlock = _extractBlock(optBlock, "learning_rate");
  if (!lrBlock) return {};
  const kinds = [
    "cosine_decay_learning_rate",
    "exponential_decay_learning_rate",
    "manual_step_learning_rate",
    "polynomial_decay_learning_rate",
    "constant_learning_rate",
  ];
  for (const k of kinds) {
    const b = _extractBlock(lrBlock, k);
    if (!b) continue;
    const out = { "lr.schedule": k };
    const base   = _getNum(b, "learning_rate_base") ?? _getNum(b, "learning_rate");
    const total  = _getNum(b, "total_steps");
    const warm   = _getNum(b, "warmup_learning_rate");
    const warmSt = _getNum(b, "warmup_steps");
    const decay  = _getNum(b, "decay_steps");
    const power  = _getNum(b, "power");
    const gamma  = _getNum(b, "gamma");
    if (base)   out["lr.base"] = base;
    if (total)  out["lr.total_steps"] = total;
    if (warm)   out["lr.warmup"] = warm;
    if (warmSt) out["lr.warmup_steps"] = warmSt;
    if (decay)  out["lr.decay_steps"] = decay;
    if (power)  out["lr.power"] = power;
    if (gamma)  out["lr.gamma"] = gamma;
    return out;
  }
  return { "lr.schedule": "unknown" };
}

// ---------- main rich parser ----------
export function parsePipelineConfigRich(configText) {
  const src = String(configText || "").replace(/\r\n/g, "\n");
  const out = {};

  // --- model/ssd ---
  const ssd = _extractNested(src, ["model", "ssd"]);
  if (ssd) {
    const num_classes = _getNum(ssd, "num_classes");
    if (num_classes) out["num_classes"] = num_classes;

    // feature_extractor (+ BiFPN)
    const fe = _extractBlock(ssd, "feature_extractor");
    if (fe) {
      const feType = _getStr(fe, "type");
      if (feType) out["feature_extractor.type"] = feType;
      const bifpn = _extractBlock(fe, "bifpn");
      if (bifpn) {
        const minL = _getNum(bifpn, "min_level");
        const maxL = _getNum(bifpn, "max_level");
        const it   = _getNum(bifpn, "num_iterations");
        const nf   = _getNum(bifpn, "num_filters");
        if (minL) out["bifpn.min_level"] = minL;
        if (maxL) out["bifpn.max_level"] = maxL;
        if (it)   out["bifpn.num_iterations"] = it;
        if (nf)   out["bifpn.num_filters"] = nf;
      }
    }

    // image_resizer
    const ir = _extractNested(src, ["model", "ssd", "image_resizer", "keep_aspect_ratio_resizer"]);
    if (ir) {
      const minD = _getNum(ir, "min_dimension");
      const maxD = _getNum(ir, "max_dimension");
      const pad  = _getBool(ir, "pad_to_max_dimension");
      if (minD) out["image_size.min"] = minD;
      if (maxD) out["image_size.max"] = maxD;
      if (pad)  out["image_size.pad_to_max"] = pad;
    }

    // anchors
    const ag = _extractNested(src, ["model", "ssd", "anchor_generator", "multiscale_anchor_generator"]);
    if (ag) {
      const minL = _getNum(ag, "min_level");
      const maxL = _getNum(ag, "max_level");
      const sc   = _getNum(ag, "anchor_scale");
      const ar   = _getArr(ag, "aspect_ratios");
      const spo  = _getNum(ag, "scales_per_octave");
      if (minL) out["anchors.min_level"] = minL;
      if (maxL) out["anchors.max_level"] = maxL;
      if (sc)   out["anchors.scale"] = sc;
      if (ar)   out["anchors.aspect_ratios"] = ar;
      if (spo)  out["anchors.scales_per_octave"] = spo;
    }

    // matcher thresholds
    const mt = _extractNested(src, ["model", "ssd", "matcher", "argmax_matcher"]);
    if (mt) {
      const t1 = _getNum(mt, "matched_threshold");
      const t2 = _getNum(mt, "unmatched_threshold");
      if (t1) out["matcher.matched_threshold"] = t1;
      if (t2) out["matcher.unmatched_threshold"] = t2;
    }

    // post_processing / NMS
    const nms = _extractNested(src, ["model", "ssd", "post_processing", "batch_non_max_suppression"]);
    if (nms) {
      const score = _getNum(nms, "score_threshold");
      const iou   = _getNum(nms, "iou_threshold");
      const perC  = _getNum(nms, "max_detections_per_class");
      const total = _getNum(nms, "max_total_detections");
      const soft  = _getNum(nms, "soft_nms_sigma");
      const agn   = _getBool(nms, "use_class_agnostic_nms");
      if (score) out["nms.score_threshold"] = score;
      if (iou)   out["nms.iou_threshold"] = iou;
      if (perC)  out["nms.max_per_class"] = perC;
      if (total) out["nms.max_total"] = total;
      if (soft)  out["nms.soft_sigma"] = soft;
      if (agn)   out["nms.class_agnostic"] = agn;
    }

    // loss summary
    const loss = _extractBlock(ssd, "loss");
    if (loss) {
      const cw = _getNum(loss, "classification_weight");
      const lw = _getNum(loss, "localization_weight");
      if (cw) out["loss.classification_weight"] = cw;
      if (lw) out["loss.localization_weight"] = lw;
      const cls = _extractBlock(loss, "classification_loss");
      const loc = _extractBlock(loss, "localization_loss");
      const focal = cls && _extractBlock(cls, "weighted_sigmoid_focal");
      if (focal) {
        const alpha = _getNum(focal, "alpha");
        const gamma = _getNum(focal, "gamma");
        if (alpha) out["loss.focal.alpha"] = alpha;
        if (gamma) out["loss.focal.gamma"] = gamma;
      }
      if (loc && _extractBlock(loc, "weighted_smooth_l1")) {
        out["loss.localization"] = "weighted_smooth_l1";
      }
    }
  }

  // --- train_config ---
  const train = _extractBlock(src, "train_config");
  if (train) {
    const ckpt = _getStr(train, "fine_tune_checkpoint");
    if (ckpt) {
      const m = ckpt.match(/ckpt-(\d+)/);
      out["fine_tune_checkpoint"] = m ? m[1] : _basename(ckpt); // -> "0" for ckpt-0
    }
    const bs = _getNum(train, "batch_size"); if (bs) out["batch_size"] = bs;
    const ns = _getNum(train, "num_steps");  if (ns) out["num_steps"]  = ns;

    const opt = _extractBlock(train, "optimizer");
    if (opt) Object.assign(out, _parseLearningRate(opt));
  }

  // --- readers ---
  const tri = _extractBlock(src, "train_input_reader");
  if (tri) {
    const lm  = _getStr(tri, "label_map_path");
    const tip = _getStr(_extractBlock(tri, "tf_record_input_reader") || tri, "input_path");
    if (lm)  out["train.label_map_path"] = _basename(lm);
    if (tip) out["train.input_path"]     = _basename(tip).replace(/^"+|"+$/g, "");
  }
  const eri = _extractBlock(src, "eval_input_reader");
  if (eri) {
    const lm  = _getStr(eri, "label_map_path");
    const eip = _getStr(_extractBlock(eri, "tf_record_input_reader") || eri, "input_path");
    if (lm)  out["eval.label_map_path"] = _basename(lm);
    if (eip) out["eval.input_path"]     = _basename(eip).replace(/^"+|"+$/g, "");
  }

  // --- eval_config ---
  const ec = _extractBlock(src, "eval_config");
  if (ec) {
    const ms = _getStr(ec, "metrics_set"); if (ms) out["eval.metrics_set"] = ms;
    const bs = _getNum(ec, "batch_size");  if (bs) out["eval.batch_size"]  = bs;
  }

  return out;
}

// Build exactly what the dialog should show (ordered, no block headers)
export function buildConfigDisplay(parsed, stepFolder = "") {
  const p = parsed || {};
  const rows = {};

  // Top 3 requested
  if (p["eval.label_map_path"]) rows["label_map_path"] = p["eval.label_map_path"];
  if (p["eval.input_path"])     rows["input_path"]     = p["eval.input_path"];
  if (p["fine_tune_checkpoint"]!= null) rows["fine_tune_checkpoint"] = String(p["fine_tune_checkpoint"]);

  // Training knobs
  if (p["num_classes"]) rows["num_classes"] = p["num_classes"];
  if (p["batch_size"])  rows["batch_size"]  = p["batch_size"];
  if (p["num_steps"])   rows["num_steps"]   = p["num_steps"];

  // LR summary
  if (p["lr.schedule"])     rows["lr.schedule"]      = p["lr.schedule"];
  if (p["lr.base"])         rows["lr.base"]          = p["lr.base"];
  if (p["lr.total_steps"])  rows["lr.total_steps"]   = p["lr.total_steps"];
  if (p["lr.warmup"])       rows["lr.warmup"]        = p["lr.warmup"];
  if (p["lr.warmup_steps"]) rows["lr.warmup_steps"]  = p["lr.warmup_steps"];

  // Reader info
  if (p["train.label_map_path"]) rows["train.label_map_path"] = p["train.label_map_path"];
  if (p["train.input_path"])     rows["train.input_path"]     = p["train.input_path"];
  if (p["eval.metrics_set"])     rows["eval.metrics_set"]     = p["eval.metrics_set"];
  if (p["eval.batch_size"])      rows["eval.batch_size"]      = p["eval.batch_size"];

  // Model / FE basics
  if (p["feature_extractor.type"]) rows["feature_extractor.type"] = p["feature_extractor.type"];
  if (p["bifpn.min_level"])        rows["bifpn.min_level"]        = p["bifpn.min_level"];
  if (p["bifpn.max_level"])        rows["bifpn.max_level"]        = p["bifpn.max_level"];
  if (p["bifpn.num_iterations"])   rows["bifpn.num_iterations"]   = p["bifpn.num_iterations"];
  if (p["bifpn.num_filters"])      rows["bifpn.num_filters"]      = p["bifpn.num_filters"];

  // Image size
  if (p["image_size.min"])         rows["image_size.min"]         = p["image_size.min"];
  if (p["image_size.max"])         rows["image_size.max"]         = p["image_size.max"];
  if (p["image_size.pad_to_max"])  rows["image_size.pad_to_max"]  = p["image_size.pad_to_max"];

  // Anchors
  if (p["anchors.min_level"])      rows["anchors.min_level"]      = p["anchors.min_level"];
  if (p["anchors.max_level"])      rows["anchors.max_level"]      = p["anchors.max_level"];
  if (p["anchors.scale"])          rows["anchors.scale"]          = p["anchors.scale"];
  if (p["anchors.aspect_ratios"])  rows["anchors.aspect_ratios"]  = p["anchors.aspect_ratios"];
  if (p["anchors.scales_per_octave"]) rows["anchors.scales_per_octave"] = p["anchors.scales_per_octave"];

  // Matcher + NMS
  if (p["matcher.matched_threshold"])   rows["matcher.matched_threshold"]   = p["matcher.matched_threshold"];
  if (p["matcher.unmatched_threshold"]) rows["matcher.unmatched_threshold"] = p["matcher.unmatched_threshold"];
  if (p["nms.score_threshold"])         rows["nms.score_threshold"]         = p["nms.score_threshold"];
  if (p["nms.iou_threshold"])           rows["nms.iou_threshold"]           = p["nms.iou_threshold"];
  if (p["nms.max_per_class"])           rows["nms.max_per_class"]           = p["nms.max_per_class"];
  if (p["nms.max_total"])               rows["nms.max_total"]               = p["nms.max_total"];
  if (p["nms.soft_sigma"])              rows["nms.soft_sigma"]              = p["nms.soft_sigma"];
  if (p["nms.class_agnostic"])          rows["nms.class_agnostic"]          = p["nms.class_agnostic"];

  // Loss (quick summary)
  if (p["loss.classification_weight"])  rows["loss.classification_weight"]  = p["loss.classification_weight"];
  if (p["loss.localization_weight"])    rows["loss.localization_weight"]    = p["loss.localization_weight"];
  if (p["loss.focal.alpha"])            rows["loss.focal.alpha"]            = p["loss.focal.alpha"];
  if (p["loss.focal.gamma"])            rows["loss.focal.gamma"]            = p["loss.focal.gamma"];
  if (p["loss.localization"])           rows["loss.localization"]           = p["loss.localization"];


  if (stepFolder) rows["checkpoint_folder"] = stepFolder;

  return rows;
}